
import { useEffect, useId, useState } from "react";
import tinycolor from "tinycolor2";

import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PiggyBank, Sun, Timer, Wallet, Zap } from "lucide-react";

import KpiTiles from "@/components/KpiTiles";
import { fetchPvPotential, PvAssumptions, PvPotential } from "../../PVPotentialAPI";

class Properties {
  building: any;
}

const efficiency = [
  [87, 87, 87, 87, 87, 87, 87, 87, 87, 87, 87, 87, 87, 87, 87, 87, 87, 87, 87,],
  [93, 93, 93, 92, 92, 91, 90, 89, 88, 86, 85, 84, 86, 81, 81, 80, 79, 79, 79,],
  [97, 97, 97, 96, 95, 93, 91, 89, 87, 85, 82, 80, 77, 75, 73, 71, 70, 70, 70,],
  [100, 99, 99, 97, 96, 94, 91, 88, 85, 82, 79, 75, 72, 69, 66, 64, 62, 61, 61,],
  [100, 99, 99, 97, 95, 93, 90, 86, 83, 79, 75, 71, 67, 63, 59, 56, 54, 52, 52,],
  [98, 97, 96, 95, 93, 90, 87, 83, 79, 75, 70, 66, 61, 56, 52, 48, 45, 44, 43,],
  [94, 93, 92, 91, 88, 85, 82, 78, 74, 70, 65, 60, 55, 50, 46, 41, 38, 36, 35,],
  [88, 87, 86, 85, 82, 79, 76, 72, 68, 63, 58, 54, 49, 44, 39, 35, 32, 29, 28,],
  [80, 79, 78, 77, 75, 72, 68, 65, 61, 59, 51, 47, 42, 37, 33, 29, 26, 24, 23,],
  [69, 69, 69, 67, 65, 63, 60, 56, 53, 48, 44, 40, 35, 31, 27, 24, 21, 19, 18,],
];

/** Farbrampe der Wirkungsgrad-Matrix — bewusst unverändert aus dem Bestand. */
const getColor = (value: number) => {
  return value === 999 ? '#FFFFFF' : tinycolor('#00FF00').spin(-(100 - value) / 100 * 180).setAlpha(0.3).toRgbString();
}

const nf = (v: number | null | undefined, digits = 0) =>
  v == null ? "—" : new Intl.NumberFormat("de-DE", { maximumFractionDigits: digits }).format(v);

const ORIENTATION: Record<string, string> = {
  N: "Nord", NE: "Nordost", E: "Ost", SE: "Südost", S: "Süd", SW: "Südwest", W: "West", NW: "Nordwest", flach: "Flachdach",
};

/** Zelle der Matrix für eine Dachteilfläche: Neigung auf 10° gerundet, Abweichung der Ausrichtung von Süd. */
const cellOf = (tilt: number, azimuth: number | null) => {
  const deviation = azimuth == null ? 0 : Math.abs((((azimuth - 180) % 360) + 540) % 360 - 180);
  return `${Math.min(9, Math.round(tilt / 10))}-${Math.min(18, Math.round(deviation / 10))}`;
};

/** Annahmen, die im Reiter änderbar sind — in der Einheit, in der man sie denkt (ct statt €, % statt Anteil). */
const EDITABLE: { key: keyof PvAssumptions; label: string; unit: string; scale: number; hint: string }[] = [
  { key: "electricityPrice", label: "Strompreis", unit: "ct/kWh", scale: 100, hint: "Bezugspreis, den der Eigenverbrauch ersetzt" },
  { key: "selfConsumptionShare", label: "Eigenverbrauch", unit: "%", scale: 100, hint: "Anteil des Ertrags, der im Gebäude verbraucht wird" },
  { key: "feedInTariff", label: "Einspeisevergütung", unit: "ct/kWh", scale: 100, hint: "EEG, Teileinspeisung" },
  { key: "kWpPerM2", label: "Leistung je m² Modul", unit: "kWp/m²", scale: 1, hint: "heutige Module etwa 0,2" },
  { key: "performanceRatio", label: "Performance Ratio", unit: "%", scale: 100, hint: "Verluste von Wechselrichter, Leitungen, Temperatur" },
  { key: "operatingCostShare", label: "Betriebskosten", unit: "% der Investition/Jahr", scale: 100, hint: "Versicherung, Wartung, Zähler" },
];

export default function PVPotentialProperties(properties: Properties) {
  const fieldPrefix = useId();
  const buildingId = properties.building?._id ? String(properties.building._id) : properties.building?.id;
  const [pv, setPv] = useState<PvPotential | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [overrides, setOverrides] = useState<Partial<PvAssumptions>>({});

  useEffect(() => {
    if (!buildingId) return;
    let cancelled = false;
    // Entprellt: jede Eingabe in den Annahmen rechnet neu, aber nicht bei jedem Tastendruck.
    const timer = setTimeout(() => {
      setLoading(true);
      fetchPvPotential(buildingId, overrides)
        .then(result => { if (!cancelled) { setPv(result); setError(null); } })
        .catch(e => { if (!cancelled) setError(e?.message ?? String(e)); })
        .finally(() => { if (!cancelled) setLoading(false); });
    }, Object.keys(overrides).length ? 400 : 0);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [buildingId, overrides]);

  if (error && (!pv || !pv.available)) return <p className="text-sm text-destructive">PV-Potenzial konnte nicht berechnet werden: {error}</p>;
  if (!pv) return <p className="text-sm text-muted-foreground">{loading ? "Berechne PV-Potenzial …" : "Kein Gebäude gewählt."}</p>;
  if (!pv.available) return <p className="text-sm text-muted-foreground">{pv.reason}.</p>;

  const t = pv.totals!;
  const e = pv.economics!;
  const surfaces = pv.surfaces ?? [];
  const marked = new Set(surfaces.filter(s => !s.excluded).map(s => cellOf(s.tilt, s.azimuth)));
  const value = (key: keyof PvAssumptions, scale: number) => {
    const v = (overrides[key] ?? pv.assumptions[key]) * scale;
    return String(Math.round(v * 1000) / 1000);
  };

  return <div className={`flex min-w-0 max-w-[1400px] flex-col gap-4 ${loading ? "opacity-70" : ""}`}>
    {error && <p className="text-sm text-destructive">PV-Potenzial konnte nicht berechnet werden: {error}</p>}
    <KpiTiles items={[
      { label: "Jahresertrag", value: nf(t.yieldKWh), unit: "kWh", hint: `${nf(t.specificYield)} kWh je kWp`, icon: Sun, tone: "primary", emphasis: true },
      { label: "Leistung", value: nf(t.kWp, 1), unit: "kWp", hint: `auf ${nf(t.usableArea)} von ${nf(t.roofArea)} m² Dach`, icon: Zap, tone: "primary" },
      { label: "Investition", value: nf(e.investment), unit: "€", hint: `${nf(pv.costPerKWp)} € je kWp`, icon: Wallet },
      { label: "Ersparnis", value: nf(e.savingsPerYear), unit: "€/Jahr", hint: "Eigenverbrauch und Einspeisung", icon: PiggyBank, tone: e.savingsPerYear > 0 ? "good" : "bad" },
      {
        label: "Amortisation", value: e.paybackYears == null ? "—" : nf(e.paybackYears, 1), unit: e.paybackYears == null ? undefined : "Jahre",
        hint: e.paybackYears == null ? "trägt sich nicht" : e.paybackYears <= 12 ? "innerhalb der Lebensdauer" : "lang für 20–25 Jahre Lebensdauer",
        icon: Timer, tone: e.paybackYears == null ? "bad" : e.paybackYears <= 12 ? "good" : "warn",
      },
    ]} />
    <p className="text-sm">
      Globalstrahlung am Standort <span className="font-bold">{nf(pv.radiation?.value)} kWh/m²</span> im Jahr (Mittel {pv.radiation?.period}),
      im Optimum {nf(pv.optimumYieldPerKWp)} kWh/kWp, auf diesem Dach <span className="font-bold">{nf(t.specificYield)} kWh/kWp</span>.
      Ersparnis <span className="font-bold">{nf(e.savingsPerYear)} €</span> im Jahr bei {nf(pv.costPerKWp)} €/kWp Investition.
    </p>
    {pv.existing && <p className="text-sm">
      {pv.existing.units.length
        ? <>Im Marktstammdatenregister: <span className="font-bold">{pv.existing.units.length} Anlage(n), {nf(pv.existing.kWp, 1)} kWp</span>
          {" "}({pv.existing.units.map(u => [u.name, u.commissioned && `seit ${u.commissioned.slice(0, 4)}`].filter(Boolean).join(", ")).filter(Boolean).join("; ")}). </>
        : <>Keine Anlage im Marktstammdatenregister gefunden. </>}
      <span className="text-muted-foreground">{pv.existing.note}</span>
    </p>}

    <h3 className="text-lg font-bold leading-tight text-[color:var(--customer-primary-dark,hsl(var(--foreground)))]">Ausrüstbare Dachflächen</h3>
    <p className="text-sm">Von <span className="font-bold">{nf(t.roofArea)} m²</span> Dachfläche sind <span className="font-bold">{nf(t.usableArea)} m²</span> belegbar.</p>
    <div className="overflow-x-auto">
      <table className="w-full min-w-[640px] text-sm tabular-nums">
        <thead>
          <tr className="border-b text-left text-xs text-muted-foreground">
            <th className="py-1 pr-3 font-medium">Ausrichtung</th>
            <th className="py-1 pr-3 text-right font-medium">Neigung</th>
            <th className="py-1 pr-3 text-right font-medium">Fläche</th>
            <th className="py-1 pr-3 text-right font-medium">belegbar</th>
            <th className="py-1 pr-3 text-right font-medium">Wirkungsgrad</th>
            <th className="py-1 pr-3 text-right font-medium">Leistung</th>
            <th className="py-1 pr-3 text-right font-medium">Ertrag/Jahr</th>
            <th className="py-1 font-medium"></th>
          </tr>
        </thead>
        <tbody>
          {surfaces.map((s, i) => <tr key={i} className={`border-b last:border-0 ${s.excluded ? "text-muted-foreground" : ""}`}>
            <td className="py-1 pr-3">{ORIENTATION[s.orientation] ?? s.orientation}{s.azimuth != null ? ` (${s.azimuth}°)` : ""}</td>
            <td className="py-1 pr-3 text-right">{s.tilt}°</td>
            <td className="py-1 pr-3 text-right">{nf(s.area)} m²</td>
            <td className="py-1 pr-3 text-right">{s.excluded ? "—" : `${nf(s.usableArea)} m²`}</td>
            <td className="py-1 pr-3 text-right">{nf(s.factor * 100)} %</td>
            <td className="py-1 pr-3 text-right">{s.excluded ? "—" : `${nf(s.kWp, 1)} kWp`}</td>
            <td className="py-1 pr-3 text-right">{s.excluded ? "—" : `${nf(s.yieldKWh)} kWh`}</td>
            <td className="py-1 text-xs">{s.excluded ?? ""}</td>
          </tr>)}
        </tbody>
      </table>
    </div>

    <h3 className="text-lg font-bold leading-tight text-[color:var(--customer-primary-dark,hsl(var(--foreground)))]">Effizienz nach Dachneigung und -orientierung</h3>
    <p className="text-xs text-muted-foreground">Ertrag relativ zum Optimum; umrandet die belegten Dachflächen dieses Gebäudes. Ausrichtung als Abweichung von Süd.</p>
    {/* Die Matrix ist 19 Spalten breit; auf schmalen Viewports scrollt sie
        innerhalb ihres Rahmens, statt die Seite aufzudrücken. */}
    <div className="overflow-x-auto">
      <div className="flex w-max items-center gap-1">
        <div className="text-xs font-bold [writing-mode:vertical-lr] [text-orientation:mixed]">Dachneigung</div>
        <div>
          <div className="flex justify-center text-xs font-bold">Abweichung von Süd</div>
          <div className="flex">
            <div className="w-[25px]"></div>
            {Array(19).fill(null).map((n: any, l: number) => <div key={`head-${l}`} className="flex w-[25px] items-center justify-center py-0.5">
              <div className="text-[10px] font-bold">{`${l * 10}°`}</div>
            </div>)}
          </div>
          {Array(10).fill(null).map((n: any, i: number) =>
            <div key={`row-${i}`} className="flex">
              <div className="w-[25px] text-[10px] font-bold">{i * 10}°</div>
              {Array(19).fill(null).map((n: any, l: number) => <div key={`cell-${i}-${l}`}
                className={`flex w-[25px] items-center justify-center py-0.5 ${marked.has(`${i}-${l}`) ? "outline outline-2 -outline-offset-2 outline-[color:var(--customer-primary-dark,hsl(var(--foreground)))]" : ""}`}
                style={{ backgroundColor: getColor(efficiency[i][l]) }}>
                <div className="text-[10px]">{efficiency[i][l] === 999 ? '-%' : `${efficiency[i][l]}%`}</div>
              </div>)}
            </div>)}
        </div>
      </div>
    </div>

    <h3 className="text-lg font-bold leading-tight text-[color:var(--customer-primary-dark,hsl(var(--foreground)))]">Annahmen</h3>
    <div className="grid min-w-0 gap-4 sm:grid-cols-2 lg:grid-cols-3">
      {EDITABLE.map(({ key, label, unit, scale, hint }) => <div key={key} className="flex min-w-0 flex-col gap-1.5">
        <Label htmlFor={`${fieldPrefix}-${key}`}>{label} ({unit})</Label>
        <Input id={`${fieldPrefix}-${key}`} type="number" step="any" min={0} value={value(key, scale)}
          onChange={event => {
            if (event.target.value === "") {
              setOverrides(o => {
                const { [key]: _, ...rest } = o;
                return rest;
              });
              return;
            }
            const v = Number(event.target.value);
            if (!Number.isFinite(v) || v < 0) return;
            setOverrides(o => ({ ...o, [key]: v / scale }));
          }} />
        <p className="text-xs text-muted-foreground">{hint}</p>
      </div>)}
    </div>
    <div className="text-xs text-muted-foreground">
      <p>{pv.caveat} Flachdächer als Ost-West-Aufständerung auf {nf(pv.assumptions.usableShareFlat * 100)} % der Fläche, geneigte Dächer auf {nf(pv.assumptions.usableShareSloped * 100)} %; Nordseiten unter {nf(pv.assumptions.minFactor * 100)} % Wirkungsgrad bleiben frei.</p>
      {pv.sources?.map(s => <p key={s}>{s}</p>)}
    </div>
  </div>;
}
